import { SortDescriptor, orderBy } from "@progress/kendo-data-query";
import { Grid, GridCell, GridCellProps, GridColumn, GridItemChangeEvent, GridNoRecords, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { SplitterPaneProps } from "@progress/kendo-react-layout";
import { useEffect, useMemo, useState } from "react";
import LoaderContainer from "../../commons/container/LoaderContainer";
import { dateToBdFormat, getDateWithoutTime } from "../../commons/helpers/Dates";
import { clone } from "../../commons/helpers/ObjectHelper";
import { Agence } from "../../models/Agence";
import { Equipe } from "../../models/Equipe";
import { Magasin } from "../../models/Magasin";
import { useDeleteVehiculeMutation, useGetEquipesQuery, useUpdateEquipeMutation } from "../../redux/api/cache";
import { columns } from "./Columns";
import CustomCommandCell from "./cells/CustomCommandCell";
import DinerCell from "./cells/DinerCell";
import HeureCell from "./cells/HeureCell";
import { UserCell } from "./cells/UserCell";
import { FloatingActionButton, FloatingActionButtonItemProps } from "@progress/kendo-react-buttons";
import { faTruck, faFileImport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AddVehiculeModal from "../add/AddVehiculeModal";
import AddAutreJourModal from "../add/AddAutreJourModal";
import "./deliverygrid.css";
import { useAppSelector } from "../../redux/store";

interface DeliveryGridProps {
    date: Date;
    magasin: Magasin;
    itemInEdit: boolean;
    setItemInEdit: React.Dispatch<React.SetStateAction<boolean>>;
    agence: Agence;
    panes: SplitterPaneProps[];
}

export default function DeliveryGrid(props: DeliveryGridProps) {
    const editField = "inEdit";
    const [showAddModalVehicule, setShowAddModalVehicule] = useState<boolean>(false);
    const [showAddModalAutreJour, setShowAddModalAutreJour] = useState<boolean>(false);
    const floatingOptions = [{
        image: faTruck, setShow: setShowAddModalVehicule
    }, { image: faFileImport, setShow: setShowAddModalAutreJour }]
    const [sort, setSort] = useState<SortDescriptor[]>([{ field: "vehicule" }]);
    const { data: equipes = [], isLoading: isEquipesLoading, isFetching: isEquipesFetching } = useGetEquipesQuery({ agence: props.agence.agence_cd, date: dateToBdFormat(props.date), mag_num: props.magasin.mag_num }, { skip: props.magasin.mag_num === -1 || !props.agence.agence_cd, refetchOnMountOrArgChange: true });
    const [updateEquipe] = useUpdateEquipeMutation();
    const [deleteVehicule] = useDeleteVehiculeMutation();
    const [filteredData, setFilteredData] = useState<Equipe[]>(clone(equipes));
    const d_liv69 = useAppSelector(state => state.appState.d_liv69);

    const UserCellWrapper = (gridProps: GridCellProps) => {
        return <UserCell gridCellProps={gridProps} date={props.date} magasin={props.magasin} agence={props.agence} />
    };


    const CommandCell = (gridCellProps: GridCellProps) => (
        <CustomCommandCell
            {...gridCellProps}
            cancel={cancel}
            edit={enterEdit}
            editField={editField}
            update={update}
            deleteVehicule={handleDeleteVehicule}
            itemInEdit={props.itemInEdit}
            date={props.date}
        />
    );

    const customCells = new Map<string, (gridProps: GridCellProps) => JSX.Element>([
        ["userCell", UserCellWrapper],
        ["dinerCell", DinerCell],
        ["heureCell", HeureCell],
        ["commandCell", CommandCell]
    ])

    useEffect(() => {
        if (equipes.length !== 0 || filteredData.length !== 0) {
            setFilteredData(clone(equipes));
        }
    }, [equipes]);


    const update = async (dataItem: Equipe) => {
        dataItem.inEdit = false;
        let result = await updateEquipe({ agence: props.agence.agence_cd, date: dateToBdFormat(props.date), mag_num: props.magasin.mag_num, vehicule: dataItem.vehicule, body: dataItem });
        if (!(result as any).data)
            setFilteredData(equipes);
        props.setItemInEdit(false);
    };

    const cancel = (dataItem: Equipe) => {
        const originalItem = equipes.find(
            f => f.vehicule === dataItem.vehicule
        );
        const newData = filteredData.map(item =>
            item.vehicule === originalItem?.vehicule ? originalItem : item
        );

        setFilteredData(newData);
        props.setItemInEdit(false);
    };

    const enterEdit = (dataItem: Equipe) => {
        const newData = filteredData.map(item =>
            item.vehicule === dataItem.vehicule ? { ...item, inEdit: true } : item
        )
        setFilteredData(newData);
        props.setItemInEdit(true);
    };

    const handleDeleteVehicule = async (dataItem: Equipe) => {
        if (window.confirm(`Voulez-vous retirer le véhicule ${dataItem.vehicule} du vendredi le ${dataItem.date}?`)) {
            await deleteVehicule({ agence: props.agence.agence_cd, date: dateToBdFormat(props.date), mag_num: props.magasin.mag_num, vehicule: dataItem.vehicule });
        }
    }

    const itemChange = (event: GridItemChangeEvent) => {
        const field = event.field || '';
        const newData = filteredData.map(item =>
            item.vehicule === event.dataItem.vehicule
                ? { ...item, [field]: event.value }
                : item
        );

        setFilteredData(newData)
    };

    const gridColumns = useMemo(() => {
        return columns.map((c, i) => <GridColumn title={c.title} field={c.field} key={i} cell={c.cell ? customCells.get(c.cell) : GridCell} editable={c.editable} width={c.minWidth} minResizableWidth={c.minWidth}></GridColumn>)
    }, [filteredData]);

    const CustomItem = (props: FloatingActionButtonItemProps) => {
        return (<>
            <span className="add-item">
                <FontAwesomeIcon icon={floatingOptions[props.itemIndex].image} />
                <span className="add-details">
                    <span>{props.item.text}</span>
                </span>
            </span>
        </>);
    };

    return <>{!isEquipesFetching && !isEquipesLoading && (<><Grid data={orderBy(filteredData, sort)} sortable={{ allowUnsort: true, mode: 'single' }} resizable={true} sort={sort}
        onSortChange={(e: GridSortChangeEvent) => { if (e.sort.length === 0 || columns.find(f => f.field === e.sort[0].field)?.canInteract) setSort(e.sort); }} reorderable={false} editField={editField} onItemChange={itemChange}
        className="k-flex-grow k-h-full k-w-full k-overflow-auto">
        {gridColumns}
        <GridNoRecords>
            <p>Aucune donnée à afficher</p>
        </GridNoRecords>
    </Grid><FloatingActionButton style={{ zIndex: 2 }} icon="plus" text="Ajouter" align={{ horizontal: 'end', vertical: 'bottom' }}
        alignOffset={{ x: 50, y: 50 }} disabled={(!props.agence.agence_cd || props.magasin.mag_num === -1 || props.date.getTime() < getDateWithoutTime(new Date()).getTime()) || !d_liv69.canAdd}
        items={[{ text: "Ajouter un véhicule" }, { text: "Importer d'une autre journée" }]}
        item={CustomItem} popupSettings={{ animate: false, popupClass: 'add-list' }} positionMode="absolute" onItemClick={(e) => floatingOptions[e.itemIndex].setShow(true)} /></>)}
        {(isEquipesFetching || isEquipesLoading) && (<LoaderContainer />)}
        {showAddModalVehicule && (<AddVehiculeModal setShowAddModalVehicule={setShowAddModalVehicule} agence={props.agence} magasin={props.magasin} date={props.date} />)}
        {showAddModalAutreJour && (<AddAutreJourModal setShowAddModalAutreJour={setShowAddModalAutreJour} agence={props.agence} magasin={props.magasin} date={props.date} />)}</>;
}